import { z } from 'zod';

import { toLatinDigits } from '@/shared/utils/formatters';

/**
 * Validation building blocks shared by every form.
 *
 * Each rule mirrors a backend constraint (`app/core/validators.py`, the Pydantic
 * schemas) so the client rejects exactly what the server would — no more, no
 * less. Where the backend normalises input, the schema normalises identically
 * before validating.
 */

/** Iranian mobile, normalised to `09xxxxxxxxx` — mirrors `normalize_phone`. */
export const phoneSchema = z
  .string()
  .trim()
  .transform((value) => {
    const digits = toLatinDigits(value).replace(/[\s\-()]/g, '');
    if (digits.startsWith('+98')) return `0${digits.slice(3)}`;
    if (digits.startsWith('0098')) return `0${digits.slice(4)}`;
    if (digits.startsWith('98') && digits.length === 12) return `0${digits.slice(2)}`;
    return digits;
  })
  .pipe(z.string().regex(/^09\d{9}$/, 'شماره موبایل باید با ۰۹ شروع شود و ۱۱ رقم باشد'));

/** Six-digit OTP — mirrors the backend's `^\d{6}$` field pattern. */
export const otpCodeSchema = z
  .string()
  .transform((value) => toLatinDigits(value).replace(/\D/g, ''))
  .pipe(z.string().regex(/^\d{4}$/, 'کد تأیید باید ۶ رقم باشد'));

/** UUID path/body parameters. */
export const uuidSchema = z.string().uuid('شناسه معتبر نیست');

/**
 * A positive Toman amount. `Numeric(14, 2)` on the server caps the magnitude;
 * fractions below one Toman are meaningless in this domain.
 */
export const amountSchema = z
  .number({ message: 'مبلغ را وارد کنید' })
  .int('مبلغ باید عدد صحیح باشد')
  .positive('مبلغ باید بیشتر از صفر باشد')
  .max(999_999_999_999, 'مبلغ واردشده بیش از حد مجاز است');

/** Optional free-text note, matching the 255-character column. */
export const noteSchema = z
  .string()
  .trim()
  .max(255, 'توضیحات نباید بیشتر از ۲۵۵ نویسه باشد')
  .optional()
  .or(z.literal('').transform(() => undefined));

/** Helper for the `Field(..., min_length=1, max_length=N)` pattern. */
export function nameSchema(max: number, label = 'نام') {
  return z
    .string()
    .trim()
    .min(1, `${label} را وارد کنید`)
    .max(max, `${label} نباید بیشتر از ${max} نویسه باشد`);
}

/** Jalali year/month of a charge period — mirrors `ge=1300, le=1500`. */
export const jalaliYearSchema = z
  .number()
  .int()
  .min(1300, 'سال معتبر نیست')
  .max(1500, 'سال معتبر نیست');

export const jalaliMonthSchema = z
  .number()
  .int()
  .min(1, 'ماه معتبر نیست')
  .max(12, 'ماه معتبر نیست');
